// /hr/kandidati — Potenciální trenéři (užší výběrové řízení).
//
// Kandidáti, kteří prošli prvním sítem a jdou do užšího výběru. U každého:
//   jméno, telefon, e-mail, lokalita, zdroj + poznámky (stejný editor jako v Úkolech)
//   a vstupní test (14 otázek) — šéftrenér vygeneruje odkaz a pošle ho kandidátovi.
//
// RBAC: owner + manager (stejně jako /hr/lide). Manažer smí kandidáta založit,
// poslat mu odkaz na test a vidět vyhodnocení, ale MAZAT výsledek testu ani
// kandidáta nesmí — to je jen ownera (Láďa, 6.8.2026). Hlídá i backend (403).
// Backend: /api/hr-kandidati (interní), /api/hr-kandidat-test (veřejný, pro kandidáta).

import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Users, RefreshCw, Mail, Phone, MapPin, Link2, X as XIcon, ClipboardList, CheckCircle2, AlertTriangle, Clock, Trash2, Plus, FileText, Award, ChevronRight, ChevronDown, Loader2, RotateCcw, MessageCircle, Pin, Archive } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { DlazdiceFiltr, PruhFiltru, EmptyState, SkeletonRows, ErrorNote, Modal, HlavickaOkna, RozbalovaciVyber } from "../../components/ui.jsx";
import { CommentBubble, formatCommentTime } from "../../components/Komentare.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import CopyButton from "../../components/CopyButton.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import InfoTip from "../../components/InfoTip.jsx";
import { toast } from "../../components/Toast.jsx";
import { TextPrilohaEditor, TextPrilohaViewer, smazKoncept } from "../operations/Ukoly.jsx";

// 14. 9. 2026 (fáze 6): jedna řada stavů bez Pohovoru. Hodnoty prijat a zamitnut
// v DB zůstávají, mění se jen popisky (Přijatý, Nedohodnuto). trial a blacklist jsou nové.
// `poradi` řídí řazení seznamu: Trial a Test hotový nahoře, Blacklist úplně dole.
const STAVY = [
  { id: "novy", label: "Nový", cls: "bg-slate-100 text-slate-700 border-slate-200", tecka: "bg-slate-500", poradi: 2 },
  { id: "test_odeslan", label: "Test odeslán", cls: "bg-sky-100 text-sky-700 border-sky-200", tecka: "bg-sky-500", poradi: 3 },
  { id: "test_hotovy", label: "Test hotový", cls: "bg-brand-100 text-brand-700 border-brand-200", tecka: "bg-brand", poradi: 1 },
  { id: "trial", label: "Trial", cls: "bg-amber-100 text-amber-700 border-amber-200", tecka: "bg-amber-500", poradi: 0 },
  { id: "prijat", label: "Přijatý", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", tecka: "bg-emerald-600", poradi: 5 },
  { id: "zamitnut", label: "Nedohodnuto", cls: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-400", poradi: 6 },
  { id: "blacklist", label: "Blacklist", cls: "bg-brand-950 text-white border-brand-950", tecka: "bg-brand-950", poradi: 7 },
];
// Starý stav u existujících záznamů: ve výběru se nenabízí, ale jde z něj přepnout.
const STAV_POHOVOR = { id: "pohovor", label: "Pohovor (starý stav)", cls: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-400", poradi: 4, legacy: true };
const stavInfo = (s) => (s === "pohovor" ? STAV_POHOVOR : STAVY.find((x) => x.id === s) || STAVY[0]);
const stavTlumeny = (s) => s === "zamitnut" || s === "blacklist";
const Tecka = ({ cls }) => <span aria-hidden className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cls}`} />;

const VERDIKT_CLS = {
  "Doporučeno dál": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Hraniční": "bg-amber-50 text-amber-700 border-amber-200",
  "Nedoporučeno": "bg-rose-50 text-rose-700 border-rose-200",
};

// 12.9.2026 (fáze 4c): vlastní třída polí nahrazena `.field` ze vzorníku.

function normalizeSearch(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}
function datum(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" });
  } catch { return "—"; }
}
function testOdkaz(token) {
  if (!token) return "";
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/test-trenera/${token}`;
}

/* ─── malé prvky (stejný jazyk jako /hr/lide) ─────────────────────────── */

function StavPill({ stav }) {
  const s = stavInfo(stav);
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>
      <Tecka cls={s.id === "blacklist" ? "bg-white/80" : s.tecka} />
      {s.label}
    </span>
  );
}

function TestPill({ k }) {
  if (k.test_hotovo_at) {
    const cls = VERDIKT_CLS[k.test_verdikt] || "bg-slate-50 text-slate-600 border-slate-200";
    return (
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border tabular-nums ${cls}`}>
        {k.test_skore != null ? `${k.test_skore} %` : "hotovo"}
        {k.test_verdikt ? <span className="opacity-60"> | {k.test_verdikt}</span> : null}
      </span>
    );
  }
  if (k.test_token) {
    return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200">Čeká na vyplnění</span>;
  }
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-500 border-slate-200">Neodeslán</span>;
}

/* ─── řádek v seznamu ─────────────────────────────────────────────────── */

function KandidatRow({ k, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-brand-300 hover:shadow-md transition-all min-h-[44px] sm:min-h-0 ${stavTlumeny(k.stav) ? "opacity-75" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 truncate">{k.jmeno}</span>
            <StavPill stav={k.stav} />
            <TestPill k={k} />
            {k.komentaru > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200 tabular-nums">
                <MessageCircle className="w-3.5 h-3.5" />{k.komentaru}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-slate-500 [overflow-wrap:anywhere]">
            {k.telefon && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{k.telefon}</span>}
            {k.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{k.email}</span>}
            {k.lokalita && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{k.lokalita}</span>}
            {k.pocet_poznamek > 0 && <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{k.pocet_poznamek}</span>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
      </div>
    </button>
  );
}

/* ─── modal: nový kandidát ────────────────────────────────────────────── */

function NovyKandidatModal({ onClose, onSaved }) {
  const [f, setF] = useState({ jmeno: "", telefon: "", email: "", lokalita: "", zdroj: "" });
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setF((p) => ({ ...p, [key]: e.target.value }));

  const uloz = async () => {
    if (!f.jmeno.trim()) return;
    setSaving(true);
    try {
      const r = await apiFetch("/api/hr-kandidati", { method: "POST", body: JSON.stringify(f) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      toast("Kandidát přidán", "success");
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={UserPlus} nazev="Nový kandidát" onClose={onClose} />}
      footer={
        <>
          <button onClick={onClose} className="btn btn-ghost">Zrušit</button>
          <button onClick={uloz} disabled={saving || !f.jmeno.trim()} className="btn btn-primary">
            {saving ? "Ukládám…" : "Přidat kandidáta"}
          </button>
        </>
      }
    >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Jméno a příjmení *</label>
            <input autoFocus value={f.jmeno} onChange={set("jmeno")} className="field" placeholder="Jana Nováková" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Telefon</label>
              <input value={f.telefon} onChange={set("telefon")} className="field" placeholder="605 123 456" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">E-mail</label>
              <input value={f.email} onChange={set("email")} className="field" placeholder="jana@email.cz" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Lokalita</label>
              <input value={f.lokalita} onChange={set("lokalita")} className="field" placeholder="VT GYM - Kbely" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Zdroj</label>
              <input value={f.zdroj} onChange={set("zdroj")} className="field" placeholder="Facebook" />
            </div>
          </div>
          <p className="text-xs text-slate-500">Základní informace přidáš po uložení jako poznámku v detailu.</p>
        </div>
    </Modal>
  );
}

/* ─── výsledek testu ──────────────────────────────────────────────────── */

function VysledekTestu({ k, detail, pocetOtazek }) {
  const [otevreno, setOtevreno] = useState(false);
  const sekce = k.test_sekce || {};
  const verdiktCls = VERDIKT_CLS[k.test_verdikt] || "bg-slate-50 text-slate-600 border-slate-200";

  return (
    <div className="space-y-3">
      <div className={`rounded-xl border p-3 ${verdiktCls}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs opacity-70">Celkové skóre</div>
            <div className="text-3xl font-bold tabular-nums">{k.test_skore != null ? `${k.test_skore} %` : "—"}</div>
          </div>
          <div className="text-right min-w-0">
            <div className="text-xs opacity-70">Verdikt</div>
            <div className="text-lg font-bold">{k.test_verdikt || "—"}</div>
            <div className="text-[11px] opacity-70">vyplněno {datum(k.test_hotovo_at)}</div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {["A", "B", "C"].map((sid) => {
          const s = sekce[sid];
          if (!s) return null;
          return (
            <div key={sid} className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  {s.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />}
                  <span className="font-semibold text-slate-800 text-sm truncate">{s.nazev}</span>
                  {s.klicova && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-brand-100 text-brand-700">klíčová</span>}
                </div>
                <span className="text-sm font-bold tabular-nums text-slate-700">{s.spravne}/{s.celkem} <span className="opacity-50">|</span> {s.pct} %</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${s.ok ? "bg-emerald-500" : "bg-rose-400"}`} style={{ width: `${s.pct}%` }} />
              </div>
              {s.note && <div className="text-xs text-slate-500 mt-2">{s.note}</div>}
            </div>
          );
        })}
      </div>

      {Array.isArray(detail) && detail.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setOtevreno((v) => !v)}
            className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1.5"
          >
            <ClipboardList className="w-4 h-4" />
            {otevreno ? "Skrýt odpovědi" : `Zobrazit všech ${pocetOtazek || detail.length} odpovědí`}
          </button>
          {otevreno && (
            <div className="mt-2 space-y-2">
              {detail.map((d, i) => (
                <div key={i} className={`rounded-xl border p-3 ${d.ok ? "bg-emerald-50/60 border-emerald-200" : "bg-rose-50/60 border-rose-200"}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-white/70 text-slate-600 border border-slate-200">{d.tag}</span>
                    <span className="text-xs text-slate-500 tabular-nums">Otázka {i + 1}</span>
                    {d.ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : <AlertTriangle className="w-4 h-4 text-rose-500" />}
                  </div>
                  <div className="text-sm font-medium text-slate-800 [overflow-wrap:anywhere]">{d.q}</div>
                  <div className="mt-1.5 text-xs text-slate-600 [overflow-wrap:anywhere]">
                    <div><span className="font-semibold">Odpověděl(a):</span> {d.opts?.[d.zvoleno] ?? "—"}</div>
                    {!d.ok && <div className="mt-0.5 text-emerald-700"><span className="font-semibold">Správně:</span> {d.opts?.[d.spravne] ?? "—"}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── komentáře ke kandidátovi ────────────────────────────────────────── */

// 14. 9. 2026 (fáze 6): stejná logika jako ClientCommentsPanel v ClientDetail.jsx,
// jen API cesty vedou na /api/hr-kandidati a data přicházejí z detailu kandidáta.
function KomentareKandidata({ kandidatId, initial, onChanged }) {
  const [comments, setComments] = useState(initial || []);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { action: "pin"|"archive"|"delete", comment, newValue? }
  const currentUser = getCurrentUser();
  const isOwner = jeOwner();
  const canDelete = isOwner;

  // Po každém načtení detailu se seznam srovná se serverem.
  useEffect(() => { setComments(initial || []); }, [initial]);

  const editComment = async (comment, newText) => {
    const snapshot = comments;
    const now = new Date().toISOString().substring(0, 19).replace("T", " ");
    setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, text: newText, aktualizovano: now } : c)));
    try {
      const res = await apiFetch(`/api/hr-kandidati?komentar=${encodeURIComponent(comment.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ text: newText }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setError(null);
    } catch (e) {
      setComments(snapshot);
      setError(`Úprava komentáře selhala: ${e.message}`);
      throw e;
    }
  };

  const submitDraft = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setError(null);
    const tmpId = `tmp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const now = new Date().toISOString().substring(0, 19).replace("T", " ");
    const optimistic = {
      id: tmpId,
      text,
      autor_email: currentUser?.email || "",
      autor_name: currentUser?.name || currentUser?.email || "?",
      vytvoreno: now,
      pinned: false,
      archived: false,
      aktualizovano: now,
      edit_history: [],
      _optimistic: true,
    };
    const draftSnapshot = draft;
    setComments((prev) => [...prev, optimistic]);
    setDraft("");
    setPosting(true);
    try {
      const res = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(kandidatId)}&action=komentar`, {
        method: "POST",
        body: JSON.stringify({ text }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setComments((prev) => prev.map((c) => (c.id === tmpId ? j.komentar : c)));
      onChanged?.();
    } catch (e) {
      setComments((prev) => prev.filter((c) => c.id !== tmpId));
      setDraft(draftSnapshot);
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  const askPin = (comment, newValue) => setConfirmDialog({ action: "pin", comment, newValue });
  const askArchive = (comment, newValue) => setConfirmDialog({ action: "archive", comment, newValue });
  const askDelete = (comment) => setConfirmDialog({ action: "delete", comment });
  const confirmAction = async () => {
    if (!confirmDialog) return;
    const { action, comment, newValue } = confirmDialog;
    setConfirmDialog(null);
    const snapshot = comments;
    if (action === "delete") {
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
    } else {
      setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, [action === "pin" ? "pinned" : "archived"]: newValue } : c)));
    }
    try {
      const res = await apiFetch(`/api/hr-kandidati?komentar=${encodeURIComponent(comment.id)}`, {
        method: action === "delete" ? "DELETE" : "PATCH",
        body: action === "delete" ? undefined : JSON.stringify({ [action === "pin" ? "pinned" : "archived"]: newValue }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setError(null);
      onChanged?.();
    } catch (e) {
      setComments(snapshot);
      setError(`Akce "${action}" selhala: ${e.message}`);
    }
  };

  const pinned = comments.filter((c) => c.pinned && !c.archived);
  const active = comments.filter((c) => !c.pinned && !c.archived).sort((a, b) => (a.vytvoreno || "").localeCompare(b.vytvoreno || ""));
  const archived = comments.filter((c) => c.archived).sort((a, b) => (b.vytvoreno || "").localeCompare(a.vytvoreno || ""));
  const totalActive = pinned.length + active.length;
  const bublina = (c) => (
    <CommentBubble
      key={c.id}
      comment={c}
      canEdit
      canDelete={canDelete}
      canEditThis={isOwner || c.autor_email === currentUser?.email}
      onPin={askPin}
      onArchive={askArchive}
      onDelete={askDelete}
      onEdit={editComment}
    />
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <ConfirmDialog
        open={!!confirmDialog}
        title={
          confirmDialog?.action === "pin"
            ? (confirmDialog?.newValue ? "Připnout komentář?" : "Odepnout komentář?")
            : confirmDialog?.action === "archive"
              ? (confirmDialog?.newValue ? "Skrýt do archivu?" : "Obnovit z archivu?")
              : "Trvale smazat komentář?"
        }
        description={
          confirmDialog ? (
            <div className="space-y-2 mt-2 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 italic text-slate-700">
                "{confirmDialog.comment.text.substring(0, 120)}{confirmDialog.comment.text.length > 120 ? "…" : ""}"
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-medium">{confirmDialog.comment.autor_name || confirmDialog.comment.autor_email}</span>
                {confirmDialog.comment.vytvoreno && `, ${formatCommentTime(confirmDialog.comment.vytvoreno)}`}
              </div>
              {confirmDialog.action === "pin" && (
                <div className="text-slate-500 text-xs">
                  {confirmDialog.newValue
                    ? "Komentář se zobrazí nahoře jako důležitý (žlutý badge)."
                    : "Komentář se vrátí mezi běžné aktivní komentáře."}
                </div>
              )}
              {confirmDialog.action === "archive" && (
                <div className="text-slate-500 text-xs">
                  {confirmDialog.newValue
                    ? "Komentář se skryje do archivu, ale zůstane v historii (lze obnovit)."
                    : "Komentář se vrátí mezi aktivní."}
                </div>
              )}
              {confirmDialog.action === "delete" && (
                <div className="rounded-xl border border-rose-300 bg-rose-50 p-2.5 text-xs text-rose-800 leading-relaxed">
                  <div className="font-bold mb-0.5 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Trvalé smazání
                  </div>
                  <div>
                    Komentář bude <strong>fyzicky odstraněn</strong> z databáze. Tato akce <strong>nelze vrátit zpět</strong>.
                    Pokud chceš jen schovat, zruš a zvol Archiv místo toho.
                  </div>
                </div>
              )}
            </div>
          ) : ""
        }
        confirmLabel={
          confirmDialog?.action === "pin"
            ? (confirmDialog?.newValue ? "Připnout" : "Odepnout")
            : confirmDialog?.action === "archive"
              ? (confirmDialog?.newValue ? "Skrýt do archivu" : "Obnovit")
              : "Trvale smazat"
        }
        cancelLabel="Zrušit"
        variant={
          confirmDialog?.action === "delete"
            ? "danger"
            : (confirmDialog?.action === "archive" && confirmDialog?.newValue ? "warning" : "default")
        }
        onConfirm={confirmAction}
        onCancel={() => setConfirmDialog(null)}
      />

      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-brand-500" />
          <span className="text-sm font-bold text-slate-800">Komentáře</span>
          {totalActive > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-brand-50 text-brand-700 border border-brand-200 tabular-nums">{totalActive}</span>
          )}
          {pinned.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 tabular-nums">
              <Pin className="w-3 h-3" /> {pinned.length}
            </span>
          )}
        </div>
      </div>

      {/* Vstup: Enter odešle, Shift+Enter nový řádek. */}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              submitDraft();
            }
          }}
          placeholder="Napiš komentář, Enter odešle"
          rows={1}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
          }}
          disabled={posting}
          data-gramm="false"
          className="field min-w-0 flex-1 resize-none disabled:opacity-50"
          style={{ maxHeight: 150 }}
        />
        <button
          type="button"
          onClick={submitDraft}
          disabled={!draft.trim() || posting}
          className="btn btn-sm btn-primary shrink-0"
          title="Odeslat (Enter)"
        >
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Komentář
        </button>
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {totalActive === 0 && archived.length === 0 && (
        <div className="mt-3 text-xs text-slate-400">Zatím žádné komentáře.</div>
      )}

      <div className="mt-3 space-y-2.5">
        {pinned.length > 0 && <div className="space-y-2.5">{pinned.map(bublina)}</div>}
        {pinned.length > 0 && active.length > 0 && (
          <div className="flex items-center gap-2 my-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ostatní</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        )}
        {active.map(bublina)}
        {archived.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setShowArchive((v) => !v)}
              aria-expanded={showArchive}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 min-h-[44px] sm:min-h-0"
            >
              <Archive className="h-4 w-4 shrink-0 text-slate-400" />
              <span>Archiv ({archived.length})</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${showArchive ? "rotate-180" : ""}`} />
            </button>
            {showArchive && <div className="space-y-2.5 mt-3">{archived.map(bublina)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── detail kandidáta ────────────────────────────────────────────────── */

// Owner (i při impersonaci si drží _realRole) smí mazat, ostatní ne.
function jeOwner() {
  const u = getCurrentUser();
  return String((u && (u._realRole || u.role)) || "").toLowerCase() === "owner";
}

function KandidatDetail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null);          // rozeditovaná kontaktní pole
  const [savingEdit, setSavingEdit] = useState(false);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [textEditor, setTextEditor] = useState(null);
  const [textView, setTextView] = useState(null);
  const [savingText, setSavingText] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setErr("");
    try {
      const r = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(id)}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData(j);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const k = data?.kandidat;
  const poznamky = data?.poznamky || [];

  const patchKandidat = async (payload) => {
    const r = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || "Uložení selhalo");
    await load();
    onChanged();
  };

  const ulozKontakty = async () => {
    setSavingEdit(true);
    try { await patchKandidat(edit); setEdit(null); toast("Uloženo", "success"); }
    catch (e) { toast(e.message, "error"); }
    finally { setSavingEdit(false); }
  };

  const zmenStav = async (stav) => {
    try { await patchKandidat({ stav }); }
    catch (e) { toast(e.message, "error"); }
  };

  const vygenerujOdkaz = async () => {
    setTokenBusy(true);
    try {
      const r = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(id)}&action=token`, { method: "POST" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Generování selhalo");
      await load();
      onChanged();
      toast("Odkaz na test vytvořen", "success");
    } catch (e) { toast(e.message, "error"); }
    finally { setTokenBusy(false); }
  };

  // Klíč konceptu poznámky, stejná logika jako u úkolů (2.10.2026).
  const klicKonceptu = `kandidat-${id}-${textEditor?.poznamkaId || "novy"}`;
  const saveText = async (nazev, obsah) => {
    setSavingText(true);
    try {
      const url = textEditor?.poznamkaId
        ? `/api/hr-kandidati?poznamka=${encodeURIComponent(textEditor.poznamkaId)}`
        : `/api/hr-kandidati?id=${encodeURIComponent(id)}&action=poznamka`;
      const r = await apiFetch(url, { method: textEditor?.poznamkaId ? "PATCH" : "POST", body: JSON.stringify({ nazev, obsah }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      smazKoncept(klicKonceptu);
      setTextEditor(null);
      await load();
      onChanged();
    } catch (e) { toast(e.message, "error"); }
    finally { setSavingText(false); }
  };

  const smazPoznamku = async (poznamkaId) => {
    try {
      const r = await apiFetch(`/api/hr-kandidati?poznamka=${encodeURIComponent(poznamkaId)}`, { method: "DELETE" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      await load();
      onChanged();
      toast("Poznámka smazána", "success");
    } catch (e) { toast(e.message, "error"); }
  };

  const resetTest = async () => {
    try {
      const r = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(id)}&action=reset-test`, { method: "POST" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání výsledku selhalo");
      await load();
      onChanged();
      toast("Výsledek testu smazán, kandidát může test vyplnit znovu", "success");
    } catch (e) { toast(e.message, "error"); }
  };

  const smazKandidata = async () => {
    try {
      const r = await apiFetch(`/api/hr-kandidati?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      toast("Kandidát smazán", "success");
      onChanged();
      onClose();
    } catch (e) { toast(e.message, "error"); }
  };

  const odkaz = testOdkaz(k?.test_token);

  return (
    <>
    <Modal
      open
      onClose={onClose}
      size="lg"
      plnaHlavicka
      header={
        <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 sm:h-10 sm:w-10">
              <UserPlus className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-white/70">Potenciální trenér</span>
              <span className="block truncate font-serif text-lg font-bold leading-tight">{k?.jmeno || "Načítám…"}</span>
            </span>
            {k && jeOwner() && (
              <button
                type="button"
                onClick={() => setConfirm({ typ: "kandidat" })}
                title="Smazat kandidáta"
                aria-label="Smazat kandidáta"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-rose-500/80"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              title="Zavřít"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-white/20"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      }
    >
        <div className="space-y-4">
          {loading && <SkeletonRows rows={4} />}
          {err && <ErrorNote>{err}</ErrorNote>}

          {k && (
            <>
              {/* stav: rozbalovací výběr (14. 9. 2026, fáze 6). Pohovor se nabízí jen u záznamu, který ho má. */}
              <RozbalovaciVyber
                stitek="Stav"
                aria-label="Stav výběrového řízení"
                value={k.stav}
                onChange={(id) => { if (id !== k.stav) zmenStav(id); }}
                items={(k.stav === "pohovor" ? [...STAVY, STAV_POHOVOR] : STAVY).map((s) => ({
                  key: s.id,
                  label: <span className="inline-flex items-center gap-2"><Tecka cls={s.tecka} />{s.label}</span>,
                }))}
              />

              {/* kontakty */}
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-xs font-semibold text-slate-600">Kontakt</div>
                  {!edit
                    ? <button onClick={() => setEdit({ jmeno: k.jmeno, telefon: k.telefon, email: k.email, lokalita: k.lokalita, zdroj: k.zdroj })}
                        className="text-xs font-semibold text-brand-700 hover:text-brand-800">Upravit</button>
                    : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEdit(null)} className="text-xs text-slate-500 hover:text-slate-700">Zrušit</button>
                        <button onClick={ulozKontakty} disabled={savingEdit} className="btn btn-secondary btn-sm">
                          {savingEdit ? "Ukládám…" : "Uložit"}
                        </button>
                      </div>
                    )}
                </div>
                {!edit ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700 [overflow-wrap:anywhere]">
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400 shrink-0" />{k.telefon || "—"}{k.telefon && <CopyButton value={k.telefon} title="Kopírovat telefon" />}</div>
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400 shrink-0" />{k.email || "—"}{k.email && <CopyButton value={k.email} title="Kopírovat e-mail" />}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{k.lokalita || "—"}</div>
                    <div className="flex items-center gap-2"><Award className="w-4 h-4 text-slate-400 shrink-0" />{k.zdroj || "—"}</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={edit.jmeno} onChange={(e) => setEdit({ ...edit, jmeno: e.target.value })} className="field" placeholder="Jméno" />
                    <input value={edit.telefon} onChange={(e) => setEdit({ ...edit, telefon: e.target.value })} className="field" placeholder="Telefon" />
                    <input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="field" placeholder="E-mail" />
                    <input value={edit.lokalita} onChange={(e) => setEdit({ ...edit, lokalita: e.target.value })} className="field" placeholder="Lokalita" />
                    <input value={edit.zdroj} onChange={(e) => setEdit({ ...edit, zdroj: e.target.value })} className="field" placeholder="Zdroj" />
                  </div>
                )}
              </div>

              {/* test */}
              <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-brand-500" />
                    <span className="text-sm font-bold text-slate-800">Vstupní test</span>
                    <InfoTip text="14 otázek ve třech sekcích: logika a úsudek, práce s manuálem a praktický úsudek. Kandidát ho vyplní z odkazu na mobilu i na počítači. Odpovědi ani výsledek nevidí, vyhodnocuje se na serveru a test jde odeslat jen jednou." />
                  </div>
                  <TestPill k={k} />
                </div>

                {!k.test_hotovo_at && (
                  <>
                    {k.test_token ? (
                      <div className="space-y-2">
                        <div className="text-xs text-slate-500">Odkaz pošli kandidátovi (odeslán {datum(k.test_odeslan_at)}):</div>
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                          <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="text-xs text-slate-700 truncate flex-1 [overflow-wrap:anywhere]">{odkaz}</span>
                          <CopyButton value={odkaz} title="Kopírovat odkaz na test" />
                        </div>
                        <button onClick={vygenerujOdkaz} disabled={tokenBusy} className="btn btn-ghost btn-sm">
                          Vygenerovat nový odkaz (starý přestane platit)
                        </button>
                      </div>
                    ) : (
                      <button onClick={vygenerujOdkaz} disabled={tokenBusy}
                        className="btn btn-primary">
                        {tokenBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Vytvořit odkaz na test
                      </button>
                    )}
                  </>
                )}

                {k.test_hotovo_at && (
                  <>
                    <VysledekTestu k={k} detail={data?.testDetail} pocetOtazek={data?.pocetOtazek} />
                    {jeOwner() && (
                    <div className="pt-1 border-t border-slate-100">
                      <button
                        onClick={() => setConfirm({ typ: "test" })}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 font-semibold transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" /> Smazat výsledek testu
                      </button>
                      <div className="text-[11px] text-slate-400 mt-1">
                        Smaže jen vyplněné odpovědi a skóre. Odkaz zůstane platný a kandidát může test vyplnit znovu.
                      </div>
                    </div>
                    )}
                  </>
                )}
              </div>

              {/* komentáře (14. 9. 2026, fáze 6) */}
              <KomentareKandidata kandidatId={id} initial={data?.komentare} onChanged={onChanged} />

              {/* poznámky */}
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-brand-500" />
                    <span className="text-sm font-bold text-slate-800">Poznámky</span>
                  </div>
                  <button onClick={() => setTextEditor({})}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl bg-brand-50 hover:bg-brand-100 text-brand-700 font-semibold border border-brand-200">
                    <Plus className="w-3.5 h-3.5" /> Přidat poznámku
                  </button>
                </div>
                {poznamky.length === 0 && <div className="text-xs text-slate-400">Zatím žádné poznámky.</div>}
                <div className="space-y-2">
                  {poznamky.map((p) => (
                    <button key={p.id} type="button" onClick={() => setTextView(p)}
                      className="w-full text-left bg-slate-50 hover:bg-brand-50 border border-slate-200 hover:border-brand-200 rounded-xl px-3 py-2 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                        <span className="text-sm font-semibold text-slate-800 truncate">{p.nazev}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{p.autor || "—"} <span className="opacity-50">|</span> {datum(p.created_at)}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
    </Modal>

      {textEditor && (
        <TextPrilohaEditor
          key={klicKonceptu}
          initialNazev={textEditor.nazev || ""}
          initialObsah={textEditor.obsah || ""}
          saving={savingText}
          draftKey={klicKonceptu}
          onSave={saveText}
          onClose={() => setTextEditor(null)}
        />
      )}
      {textView && (
        <TextPrilohaViewer
          priloha={textView}
          canEdit
          onEdit={() => { setTextEditor({ poznamkaId: textView.id, nazev: textView.nazev, obsah: textView.obsah }); setTextView(null); }}
          onDelete={() => { const pid = textView.id; setTextView(null); setConfirm({ typ: "poznamka", id: pid }); }}
          onClose={() => setTextView(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.typ === "kandidat" ? "Smazat kandidáta?"
          : confirm?.typ === "test" ? "Smazat výsledek testu?"
          : "Smazat poznámku?"}
        description={confirm?.typ === "kandidat"
          ? `Opravdu smazat ${k?.jmeno || "kandidáta"} včetně všech poznámek a výsledku testu? Tuto akci nelze vrátit.`
          : confirm?.typ === "test"
          ? `Smaže se skóre, verdikt i všechny odpovědi ${k?.jmeno || "kandidáta"}. Odkaz na test zůstane platný a kandidát ho může vyplnit znovu. Tuto akci nelze vrátit.`
          : "Opravdu smazat tuto poznámku? Tuto akci nelze vrátit."}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c?.typ === "kandidat") smazKandidata();
          else if (c?.typ === "test") resetTest();
          else if (c?.id) smazPoznamku(c.id);
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

/* ─── stránka ─────────────────────────────────────────────────────────── */

export default function HRKandidati() {
  const { data, loading, error, refetch, refreshing } = useApiData("/api/hr-kandidati");
  const [filtr, setFiltr] = useState("aktivni");
  const [stavFiltr, setStavFiltr] = useState("vse"); // 14. 9. 2026 (fáze 6): rozbalovací filtr stavu
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const kandidati = data?.kandidati || [];
  const counts = data?.counts || { total: 0, cekaji: 0, hotovo: 0, doporuceni: 0 };

  const filtrovani = useMemo(() => {
    const needle = normalizeSearch(q);
    const vysledek = kandidati.filter((k) => {
      // Dlaždice Aktivní schovává Nedohodnuto a Blacklist, ale výběr konkrétního
      // stavu v rozbalovacím filtru má přednost, jinak by se Blacklist nedal ukázat.
      if (filtr === "aktivni" && stavFiltr === "vse" && stavTlumeny(k.stav)) return false;
      if (filtr === "cekaji" && (k.test_hotovo_at || stavTlumeny(k.stav))) return false;
      if (filtr === "trial" && k.stav !== "trial") return false;
      if (filtr === "prijati" && k.stav !== "prijat") return false;
      if (stavFiltr !== "vse" && k.stav !== stavFiltr) return false;
      if (!needle) return true;
      return normalizeSearch(`${k.jmeno} ${k.email} ${k.telefon} ${k.lokalita} ${k.zdroj}`).includes(needle);
    });
    // Řazení podle stavu (Trial a Test hotový nahoře, Blacklist dole), uvnitř stavu pořadí ze serveru.
    return vysledek
      .map((k, i) => ({ k, i }))
      .sort((a, b) => (stavInfo(a.k.stav).poradi - stavInfo(b.k.stav).poradi) || (a.i - b.i))
      .map((x) => x.k);
  }, [kandidati, filtr, stavFiltr, q]);

  const pocty = useMemo(() => ({
    aktivni: kandidati.filter((k) => !stavTlumeny(k.stav)).length,
    trial: kandidati.filter((k) => k.stav === "trial").length,
    prijati: kandidati.filter((k) => k.stav === "prijat").length,
  }), [kandidati]);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* Hlavička */}
      <GradientHeader
        title="Potenciální trenéři"
        icon={UserPlus}
        napoveda="Potenciální trenéři, kteří prošli do užšího výběru. U každého vedeš kontakt, poznámky a vstupní test. Odkaz na test pošleš kandidátovi, vyplní ho na mobilu i na počítači a výsledek se sem propíše sám."
        akce={[
          { klic: "pridat", ikona: Plus, popis: "Přidat kandidáta", onClick: () => setAddOpen(true) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* Čísla nahoře zároveň filtrují (rozhodnutí Č2), pod nimi hledání a filtr stavu. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <DlazdiceFiltr
          popisek="Aktivní"
          hodnota={pocty.aktivni}
          ikona={Users}
          ton="znacka"
          zapnuto={filtr === "aktivni"}
          onClick={() => setFiltr("aktivni")}
        />
        <DlazdiceFiltr
          popisek="Čeká na test"
          hodnota={counts.cekaji}
          ikona={Clock}
          ton="info"
          zapnuto={filtr === "cekaji"}
          onClick={() => setFiltr("cekaji")}
        />
        <DlazdiceFiltr
          popisek="Trial"
          hodnota={pocty.trial}
          ikona={Award}
          ton="pozor"
          zapnuto={filtr === "trial"}
          onClick={() => setFiltr("trial")}
        />
        <DlazdiceFiltr
          popisek="Přijatí"
          hodnota={pocty.prijati}
          ikona={CheckCircle2}
          ton="ok"
          zapnuto={filtr === "prijati"}
          onClick={() => setFiltr("prijati")}
        />
      </div>

      <PruhFiltru zapnuto={!!q.trim() || stavFiltr !== "vse"} onZrusit={() => { setQ(""); setStavFiltr("vse"); }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Hledat jméno, e-mail, telefon, lokalitu…"
          className="field w-full"
        />
        <RozbalovaciVyber
          stitek="Stav"
          aria-label="Filtr podle stavu"
          value={stavFiltr}
          onChange={setStavFiltr}
          items={[
            { key: "vse", label: "Vše" },
            ...STAVY.map((s) => ({ key: s.id, label: <span className="inline-flex items-center gap-2"><Tecka cls={s.tecka} />{s.label}</span> })),
          ]}
        />
      </PruhFiltru>

      {/* Seznam */}
      {loading && <SkeletonRows rows={4} />}
      {error && <ErrorNote>{String(error)}</ErrorNote>}
      {!loading && filtrovani.length === 0 && (
        <div className="glass rounded-xl">
          <EmptyState icon={UserPlus} title="Žádní kandidáti" hint="Přepni dlaždici nahoře nebo zruš hledání." />
        </div>
      )}
      <div className="space-y-2">
        {filtrovani.map((k) => (
          <KandidatRow key={k.id} k={k} onClick={() => setDetailId(k.id)} />
        ))}
      </div>

      {addOpen && <NovyKandidatModal onClose={() => setAddOpen(false)} onSaved={refetch} />}
      {detailId && <KandidatDetail id={detailId} onClose={() => setDetailId(null)} onChanged={refetch} />}
    </div>
  );
}
